import { Button, Modal } from "react-bootstrap";
import "./general-interview.modal.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose, faFileArrowUp } from "@fortawesome/free-solid-svg-icons";
import { useState, useRef } from "react";
import * as Constants from '../constants';
import { createRoomGeneral, uploadResume } from "../services/interview-api.services";
import { ProfileResumeModel } from "../models/profile-resume.model";
import { CandidatePublic } from "../models/candidate-public.model";
import { useNavigate } from "react-router-dom";

interface GeneralInterviewData {
    firstname: string;
    lastname: string;
    email: string;
    phone: string;
    status: string;
    jobTitle: string;
    resume: File | null;
}

interface GeneralInterviewProps {
    isOpen: boolean;
    onClose(): void;
    onSubmit(data: GeneralInterviewData): void;
}

const GeneralInterviewModal: React.FC<GeneralInterviewProps> = ({ isOpen, onClose, onSubmit }) => {
    const [formData, setFormData] = useState<GeneralInterviewData>({
        firstname: "",
        lastname: "",
        email: "",
        phone: "",
        status: "",
        jobTitle: "",
        resume: null
    });
    const [resumeKey, setResumeKey] = useState<string>("");
    const [errors, setErrors] = useState<Partial<GeneralInterviewData>>({});
    const [fileName, setFileName] = useState<string>("");
    const [file, setFile] = useState<File | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const navigate = useNavigate();

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, files } = e.target;
        if (name === "resume" && files) {
            setFile(files[0]);
            setFormData({ ...formData, resume: files[0] });
            setFileName(files[0].name);
        } else {
            setFormData({ ...formData, [name]: value });
        }
        // Clear error when user starts typing
        if (errors[name as keyof GeneralInterviewData]) {
            setErrors({ ...errors, [name]: "" });
        }
    };

    const handleUploadButtonClick = () => {
        fileInputRef.current?.click();
    };

    const validateForm = (): boolean => {
        const newErrors: Partial<GeneralInterviewData> = {};

        if (!formData.firstname.trim()) {
            newErrors.firstname = "First name is required";
        }
        if (!formData.lastname.trim()) {
            newErrors.lastname = "Last name is required";
        }
        if (!formData.email.trim()) {
            newErrors.email = "Email is required";
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = "Email address is invalid";
        }
        if (!formData.phone.trim()) {
            newErrors.phone = "Phone number is required";
        }
        if (!formData.jobTitle.trim()) {
            newErrors.jobTitle = "Job title is required";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (validateForm()) {

            if(file){
                uploadResume(file, fileName, "interview_ai").then((res) => {

                    console.log(res);
                    if (res) {
                        setResumeKey(res.resume_key);
                        const company_id = process.env.REACT_APP_ENVIRONMENT === "production" ? "12b1a948-80e0-4f92-859c-7ba2026ecf2d" : "ecbd7d09-d295-42b0-9bba-4f619e21b703";
                        const job_question_id = process.env.REACT_APP_ENVIRONMENT === "production" ? "9b13f125-8003-40b3-8b4d-a81c390fef7f" : "92a67014-445b-44ef-8540-b16671c61450";
                        
                        const form: ProfileResumeModel = {
                            email: formData.email,
                            first_name: formData.firstname,
                            last_name: formData.lastname,
                            latest_job_title: formData.jobTitle,
                            phone: formData.phone,
                        }
                        setFormData(formData);
                        const newCandidate: CandidatePublic = {
                            firstname: form.first_name,
                            lastname: form.last_name,
                            email: form.email,
                            phone: form.phone,
                            status: "ACTIVE",
                            jobTitle: form.latest_job_title || "N/A", // Ensure jobTitle has a fallback
                            interview_start_date: new Date().toISOString(),
                            interview_end_date: new Date().toISOString(),
                            jobExp: 0,
                            resume: { key: res.resume_key, filename: fileName, summary: "", skills: [] },
                            candidate_id: ""
                        };
    
                        createRoomGeneral(newCandidate).then((roomRes) => { // Renamed 'res' to 'roomRes' to avoid conflict
                            if (roomRes["result"]?.candidate_id && roomRes["result"]?.company_id && roomRes["result"]?.job_question_id) {
                                console.log(roomRes);
                                
                                navigate(`public/verify/${roomRes["result"].company_id}/${roomRes["result"].job_question_id}/${roomRes["result"].candidate_id}?app_id=INTERVIEW_SCOCUTOUT_GENERAL`);
                                onSubmit(formData);
                                onClose();
                            } else {
                                console.error("Failed to create room, missing IDs in response:", roomRes);
                                alert("เกิดข้อผิดพลาดในการสร้างห้องสัมภาษณ์: ข้อมูลไม่ครบถ้วน (Error creating room: Incomplete data)");
                            }
                        }).catch((err) => {
                            console.error("Error in createRoom API call:", err);
                            alert("เกิดข้อผิดพลาดในการสร้างห้องสัมภาษณ์ (Error creating interview room)");
                        })
        
                    }
                }).catch(err => {
                    console.error("Error uploading resume:", err);
                    alert("เกิดข้อผิดพลาดในการอัปโหลดเรซูเม่ (Error uploading resume)");
                });
            }
            
           
        }
    };

    const handleClose = () => {
        setFormData({
            firstname: "",
            lastname: "",
            email: "",
            phone: "",
            status: "",
            jobTitle: "",
            resume: null
        });
        setFileName("");
        setErrors({});
        onClose();
    };

    return (
        <>
            <Modal show={isOpen} onHide={handleClose} backdrop="static" centered size="lg">
                <Modal.Body>
                    <div className="general-interview-control">
                        <FontAwesomeIcon icon={faClose} onClick={handleClose} className="close-icon" />
                        <div className="general-interview-content">
                            <h3 className="general-interview-title">General Interview by AI</h3>
                            <p className="general-interview-description">
                                กรุณากรอกข้อมูลเพื่อเริ่มต้นการสัมภาษณ์งาน
                            </p>
                            
                            <form onSubmit={handleSubmit} className="general-interview-form">
                                <div className="form-row">
                                    <div className="form-group">
                                        <label htmlFor="firstname">First Name *</label>
                                        <input
                                            type="text"
                                            id="firstname"
                                            name="firstname"
                                            value={formData.firstname}
                                            onChange={handleChange}
                                            className={errors.firstname ? 'input-error' : ''}
                                            placeholder="Enter your first name"
                                        />
                                        {errors.firstname && <p className="error-message">{errors.firstname}</p>}
                                    </div>
                                    
                                    <div className="form-group">
                                        <label htmlFor="lastname">Last Name *</label>
                                        <input
                                            type="text"
                                            id="lastname"
                                            name="lastname"
                                            value={formData.lastname}
                                            onChange={handleChange}
                                            className={errors.lastname ? 'input-error' : ''}
                                            placeholder="Enter your last name"
                                        />
                                        {errors.lastname && <p className="error-message">{errors.lastname}</p>}
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="email">Email *</label>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        className={errors.email ? 'input-error' : ''}
                                        placeholder="Enter your email address"
                                    />
                                    {errors.email && <p className="error-message">{errors.email}</p>}
                                </div>

                                <div className="form-group">
                                    <label htmlFor="phone">Phone Number *</label>
                                    <input
                                        type="tel"
                                        id="phone"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        className={errors.phone ? 'input-error' : ''}
                                        placeholder="Enter your phone number"
                                    />
                                    {errors.phone && <p className="error-message">{errors.phone}</p>}
                                </div>

                                <div className="form-group">
                                    <label htmlFor="jobTitle">Job Title *</label>
                                    <input
                                        type="text"
                                        id="jobTitle"
                                        name="jobTitle"
                                        value={formData.jobTitle}
                                        onChange={handleChange}
                                        className={errors.jobTitle ? 'input-error' : ''}
                                        placeholder="Enter your desired job title"
                                    />
                                    {errors.jobTitle && <p className="error-message">{errors.jobTitle}</p>}
                                </div>

                                {/* Resume Upload Section */}
                                <div className="form-group">
                                    <label className="resume-label">Resume/CV (Optional)</label>
                                    <div 
                                        onClick={handleUploadButtonClick}
                                        className="resume-upload-area"
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(e) => e.key === 'Enter' && handleUploadButtonClick()}
                                    >
                                        <div className="resume-upload-content">
                                            <FontAwesomeIcon icon={faFileArrowUp} className="resume-upload-icon" />
                                            <div className="resume-upload-text">
                                                <span className="resume-upload-button-text">
                                                    {Constants.HERO_FORM_UPLOAD_RESUME_BUTTON_TEXT}
                                                </span>
                                                <input 
                                                    ref={fileInputRef} 
                                                    id="resume" 
                                                    name="resume" 
                                                    type="file" 
                                                    className="sr-only" 
                                                    onChange={handleChange} 
                                                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                                />
                                            </div>
                                            {fileName ? (
                                                <p className="resume-file-name">{fileName}</p>
                                            ) : (
                                                <p className="resume-helper-text">
                                                    {Constants.HERO_FORM_FILE_UPLOAD_HELPER_TEXT}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="button-group d-flex flex-column gap-2 w-100">
                                    <Button type="submit" className="button-std submit-button w-100">
                                        Start Interview (30 THB)
                                    </Button>
                                    <Button type="button" className="button-std cancel-button w-100" onClick={handleClose}>
                                        Cancel
                                    </Button>
                                </div>
                            </form>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>
        </>
    );
};

export default GeneralInterviewModal;
